import type { Accessor, JSX, ParentComponent } from "solid-js";
import { createSignal, Show } from "solid-js";
import { type Breadcrumb, Breadcrumbs } from "./Breadcrumbs";
import "./Header.css";
import { ChatCard } from "~/modules/askgvt/studio/help/components/ChatCard";
import { CommunityCard } from "~/modules/askgvt/studio/help/components/CommunityCard";
import { FeedbackCard } from "~/modules/askgvt/studio/help/components/FeedbackCard";
import { RoadmapCard } from "~/modules/askgvt/studio/help/components/RoadmapCard";
import { Button } from "./Button";
import { ButtonLink } from "./ButtonLink";
import { useLayoutAppKindOptional } from "./layouts/layoutProvider";
import { CostDropdown } from "./organisms/CostDropdown/CostDropdown";
import { Panel } from "./Panel";
import { HStack, VStack } from "./Stack";
import { Heading } from "./typography/Heading";

type Props = {
	title: Accessor<string> | JSX.Element;
	titleSize?: "small" | "large";
	titleFooter?: JSX.Element;
	breadCrumbs?: Breadcrumb[];
	backLink?: string | -1 | (() => void);
	description?: string | Accessor<string> | JSX.Element;
	/** Full-width content rendered below the main header row. */
	footer?: JSX.Element;
	class?: string;
	align?: "start" | "center" | "end";
	style?: JSX.CSSProperties;
	showHelp?: boolean;
	questionId?: string;
};

/**
 * Page or section header with title, breadcrumbs, optional back link, and action slot.
 *
 * [Design System →](http://localhost:5174/component/Header)
 */
export const Header: ParentComponent<Props> = (props) => {
	const appKind = useLayoutAppKindOptional();

	const renderTitle = () => {
		if (typeof props.title === "function") {
			// It's an Accessor<string>
			return props.title();
		}
		// It's a JSX.Element
		return props.title;
	};

	const renderDescription = () => {
		if (typeof props.description === "function") {
			return props.description();
		}
		return props.description;
	};
	const headerClass = () => {
		return `description ${props.backLink ? "" : "no-back"}`;
	};
	const showHelp = () => props.showHelp ?? appKind() !== "askgvt";
	return (
		<header
			style={props.style}
			class={`gvtui-header ${props.class ?? ""} ${props.titleSize === "small" ? "small-title" : ""}`}
		>
			<HStack
				fullWidth
				justify="space-between"
				align={props.titleSize === "small" ? "center" : props.align}
			>
				<div>
					<Breadcrumbs breadCrumbs={props.breadCrumbs} />
					<HStack align="center" spacing="small">
						<VStack align="start" spacing="small">
							<div
								class={`gvtui-header__title ${props.titleSize === "small" ? "small-title" : ""}`}
							>
								<VStack align="start" spacing="medium">
									<HStack align="center" spacing="large">
										<BackLink backLink={props.backLink} />
										<Heading as="h1">{renderTitle()}</Heading>
									</HStack>
									<TitleFooter
										titleFooter={props.titleFooter}
										backLink={props.backLink}
									/>
								</VStack>
							</div>
							<Show when={renderDescription()}>
								<div class={headerClass()}>{renderDescription()}</div>
							</Show>
						</VStack>
					</HStack>
				</div>

				<div class="header-actions">
					<HStack align="center" spacing="small">
						{props.children}

						<Show when={appKind() === "studio"}>
							<CostDropdown questionId={props.questionId} />
						</Show>
						<HelpButton showHelp={showHelp()} />
					</HStack>
				</div>
			</HStack>
			<Show when={props.footer}>
				<div class="gvtui-header__footer">{props.footer}</div>
			</Show>
		</header>
	);
};

const BackLink = (props: { backLink: string | -1 | (() => void) }) => {
	return (
		<Show when={props.backLink}>
			<Show
				when={typeof props.backLink === "function" ? props.backLink : undefined}
			>
				<Button
					aria-label="Back"
					variant="tertiary"
					symbol="arrow_back"
					class="gvtui-header__back"
					onClick={() => {
						const back = props.backLink as () => void;
						back();
					}}
				/>
			</Show>
			<Show when={typeof props.backLink === "string"}>
				<ButtonLink
					aria-label="Back"
					variant="tertiary"
					symbol="arrow_back"
					class="gvtui-header__back"
					href={props.backLink as string}
				/>
			</Show>
			<Show when={props.backLink === -1}>
				<Button
					aria-label="Back"
					variant="tertiary"
					symbol="arrow_back"
					class="gvtui-header__back"
					onClick={() => history.back()}
				/>
			</Show>
		</Show>
	);
};

const TitleFooter = (props: {
	titleFooter: JSX.Element;
	backLink: string | -1 | (() => void);
}) => {
	return (
		<Show when={props.titleFooter}>
			<div
				class="title-footer"
				classList={{
					"title-footer-left": !props.backLink,
				}}
			>
				{props.titleFooter}
			</div>
		</Show>
	);
};

const HelpButton = (props: { showHelp: boolean }) => {
	const [openHelpPanel, setOpenHelpPanel] = createSignal(false);
	return (
		<Show when={props.showHelp}>
			<Button
				variant="tertiary"
				symbol="support_agent"
				symbolRelativeSize="plus"
				onClick={() => setOpenHelpPanel(true)}
			>
				Help & support
			</Button>
			<Panel
				isOpen={openHelpPanel}
				onToggle={() => setOpenHelpPanel(!openHelpPanel)}
				title="Help & support"
				symbol="support_agent"
				subTitle="We're here around the clock"
			>
				<VStack spacing="none" class="help-panel-cards">
					<FeedbackCard />
					<ChatCard onClick={() => setOpenHelpPanel(false)} />
					<CommunityCard />
					<RoadmapCard />
					{/* temp remove whilst we wait for content to be created */}
					{/* <LibraryCard /> */}
				</VStack>
			</Panel>
		</Show>
	);
};
